/**-----------------------------------------------------------------------------------------
* Copyright © 2023 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the package root for more information
*-------------------------------------------------------------------------------------------*/
"use client";
import * as n from "react";
import * as o from "@progress/kendo-intl";
import { load as L } from "@progress/kendo-intl";
import { validatePackage as d } from "@progress/kendo-react-common";
import p from "prop-types";
const g = {
  name: "@progress/kendo-react-intl",
  productName: "KendoReact",
  productCodes: ["KENDOUIREACT", "KENDOUICOMPLETE"],
  publishDate: 1702644631,
  version: "",
  licensingDocsUrl: "https://www.telerik.com/kendo-react-ui/components/my-license/"
};
class c {
  /**
   * Creates a new instance of the internationalization service.
   *
   * @param locale - The locale that will be used by the internationalization methods.
   */
  constructor(e) {
    if (this.locale = e, d(g), e === "" && process.env.NODE_ENV !== "production")
      throw "Locale should not be empty string";
  }
  /**
   * Formats a string with placeholders such as `Total amount {0:c}`.
   *
   * @param format - The format string.
   * @param values - One or more values to output in the format string placeholders.
   * @return - The formatted string.
   */
  format(e, ...r) {
    return r.length === 1 && Array.isArray(r[0]) ? o.format(e, r[0], this.locale) : o.format(e, r, this.locale);
  }
  /**
   * Converts a `Date` object to a string based on the specified format. If no format is provided, the default short date format is used.
   *
   * @param value - The date which will be formatted.
   * @param format - The format string or options.
   * @return - The formatted date.
   */
  formatDate(e, r) {
    return o.formatDate(e, r, this.locale);
  }
  /**
   * Converts an object to a string based on the specified format.
   *
   * @param value - The value which will be formatted.
   * @param format - The format to use.
   * @return - The formatted object.
   */
  toString(e, r) {
    return o.toString(e, r, this.locale);
  }
  /**
   * Converts a string to a `Number`.
   *
   * @param value - The string which will be parsed.
   * @param format - The format string or options.
   * @return - The parsed number.
   */
  parseNumber(e, r) {
    return o.parseNumber(e, this.locale, r);
  }
  /**
   * Converts a string to a `Date` object based on the specified format.
   *
   * @param value - The string which will be converted.
   * @param format - The format strings or options.
   * @return - The parsed date.
   */
  parseDate(e, r) {
    return o.parseDate(e, r, this.locale);
  }
  /**
   * Converts a `Number` to a string based on the specified format.
   *
   * @param value - The number which will be formatted.
   * @param format - The format string or options.
   * @return - The formatted number.
   */
  formatNumber(e, r) {
    return o.formatNumber(e, r, this.locale);
  }
  /**
   * Returns a localized date field name based on specific `dateFieldName` options.
   *
   * @param options - The detailed configuration for the desired date field name.
   * @returns - The localized date field name from the current locale based on the option.
   */
  dateFieldName(e) {
    return o.dateFieldName(e, this.locale);
  }
  /**
   * Returns the day names from the current locale based on the option.
   *
   * @param options - The detailed configuration for the desired date format.
   * @return - The day names from the current locale based on the option.
   */
  dateFormatNames(e) {
    return o.dateFormatNames(this.locale, e);
  }
  /**
   * Splits the date format into objects which contain information about each part of the pattern.
   *
   * @param format - The format string or options.
   * @returns - The date format parts.
   */
  splitDateFormat(e) {
    return o.splitDateFormat(e, this.locale);
  }
  /**
   * Returns the number symbols from the current locale.
   *
   * @return - The number symbols from the current locale.
   */
  numberSymbols() {
    return o.numberSymbols(this.locale);
  }
  /**
   * Returns the first day index, starting from Sunday.
   *
   * @return - The index of the first day of the week (0 == Sunday).
   */
  firstDay() {
    return o.firstDay(this.locale);
  }
  /**
   * @hidden
   */
  localeInfo() {
    return o.localeInfo(this.locale);
  }
  /**
   * @hidden
   */
  localeCurrency() {
    return o.localeCurrency(this.locale);
  }
}
const s = /* @__PURE__ */ Object.create({});
class u {
  constructor(e) {
    if (this.language = e, e === "" && process.env.NODE_ENV !== "production")
      throw "Language should not be an empty string";
  }
  /* eslint-disable max-len */
  /**
   * Provides a string based on a key for the current language. When no string for the current language is available under this key, the `defaultValue` is returned.
   *
   * @param key - The key which identifies the string for the current language.
   * @param defaultValue - The default value which will be returned when no string
   * for the current language is available under the key.
   * @return - The string for the current language.
   */
  // tslint:enable:max-line-length
  toLanguageString(e, r) {
    return this.language && s[this.language] && s[this.language].hasOwnProperty(e) ? s[this.language][e] : r;
  }
}
const a = n.createContext({
  intl: new c("en"),
  localization: new u()
});
class f extends n.Component {
  /**
   * Returns an internationalization service. The method is suitable for overriding when you implement custom internationalization behavior.
   */
  getIntlService() {
    return new c(this.props.locale);
  }
  /**
   * @hidden
   */
  render() {
    return /* @__PURE__ */ n.createElement(a.Consumer, null, (e) => /* @__PURE__ */ n.createElement(a.Provider, { value: { ...e, intl: this.getIntlService() } }, this.props.children));
  }
}
f.propTypes = {
  locale: p.string
};
const m = (t, e, r) => {
  for (const i in t)
    if (t.hasOwnProperty(i)) {
      const l = [...r];
      if (l.push(i), typeof t[i] != "string")
        m(t[i], e, l);
      else {
        const h = t[i];
        Object.defineProperty(e, l.join("."), { value: h });
      }
    }
};
function E(t, e) {
  let r = s[e] = s[e] || {};
  m(t, r, []);
}
class v extends n.Component {
  /**
   * Returns a localization service. The method is suitable for overriding when you implement custom localization behavior.
   */
  getLocalizationService() {
    return new u(this.props.language);
  }
  /**
   * @hidden
   */
  render() {
    return /* @__PURE__ */ n.createElement(a.Consumer, null, (e) => /* @__PURE__ */ n.createElement(a.Provider, { value: { ...e, localization: this.getLocalizationService() } }, this.props.children));
  }
}
v.propTypes = {
  language: p.string
};
function D(t) {
  if (!t && process.env.NODE_ENV !== "production")
    throw `Passed component - ${t} is invalid.`;
  const e = t.context;
  return e && e.intl ? e.intl : new c("en");
}
function w(t) {
  if (!t && process.env.NODE_ENV !== "production")
    throw `Passed component - ${t} is invalid.`;
  const e = t.context;
  return e && e.localization ? e.localization : new u();
}
function z(t) {
  t.contextType = a;
}
function x(t) {
  t.contextType = a;
}
const C = () => n.useContext(a).intl, S = () => n.useContext(a).localization;
export {
  a as GlobalizationContext,
  f as IntlProvider,
  c as IntlService,
  v as LocalizationProvider,
  u as LocalizationService,
  L as load,
  E as loadMessages,
  s as localizationMessages,
  D as provideIntlService,
  w as provideLocalizationService,
  z as registerForIntl,
  x as registerForLocalization,
  C as useInternationalization,
  S as useLocalization
};
